import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type {
  AirportStaticInfo,
  FlightAutoCompleteSuggestion,
  PortInfo,
} from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';
import type { Coordinate } from '@libs/maps';

import type {
  AddFlightsModalInitialRoute,
  AddFligthsModalRoute,
} from '../addFlightsModal.model';
import { getSuggestedAirport } from '../addFlightsModal.helper';

import {
  mapToAddFlightsModalRoute,
  yourPositionLabel,
} from './addFlightsModalStore.helpers';

export const useAddFlighstModalStore = defineStore(
  'AddFlightsModalStore',
  () => {
    // State

    const routes = ref<AddFligthsModalRoute[]>([]);
    const editingAirport = ref<PortInfo | undefined>(undefined);
    const editingAirportIndex = ref<number | undefined>(undefined);
    const editingIsFromAirport = ref(true);
    const editing = ref(false);
    const shouldUpdateHomeAirport = ref(true);

    const isButtonDisabled = computed<boolean>(() =>
      routes.value
        .filter((route) => route.isEnabled)
        .some(
          (route) =>
            !route.fromAirport.code ||
            !route.toAirport.code ||
            route.fromAirport.code === route.toAirport.code
        )
    );
    const isEditing = computed<boolean>(() => editing.value);
    const isEditingFrom = computed<boolean>(() => editingIsFromAirport.value);
    // Actions

    function initializeRoutes(
      initialRoutes: AddFlightsModalInitialRoute[],
      startingPostion: Coordinate | undefined,
      endPosition: Coordinate | undefined
    ): void {
      routes.value = [];
      initialRoutes.forEach(
        (initialRoute: AddFlightsModalInitialRoute, index: number): void => {
          routes.value.push(mapToAddFlightsModalRoute(initialRoute));

          getSuggestedAirport(
            initialRoute.from?.searchText || startingPostion
          ).subscribe((airport: AirportStaticInfo | undefined) => {
            if (routes.value && routes.value[index] && airport) {
              const mappedAriport = {
                code: airport.code,
                name: airport.city,
                label: airport.label,
              };
              setAriport(index, true, mappedAriport);
            }
          });

          getSuggestedAirport(
            initialRoute.to?.searchText || endPosition
          ).subscribe((airport: AirportStaticInfo | undefined) => {
            if (routes.value && routes.value[index] && airport) {
              const mappedAriport = {
                code: airport.code,
                name: airport.city,
                label: airport.label,
              };
              setAriport(index, false, mappedAriport);
            }
          });
        }
      );
    }

    function toggleRoute(route: AddFligthsModalRoute): void {
      route.isEnabled = !route.isEnabled;
    }

    function toggleDirectFlight(route: AddFligthsModalRoute): void {
      route.isDirect = !route.isDirect;
    }

    function setAriport(
      index: number,
      isFromAirport: boolean,
      airport: PortInfo
    ): void {
      const currentRoute = routes.value[index];

      assertValue(
        currentRoute,
        'addFlightsModalStore > setAirport > currentRoute'
      );

      if (isFromAirport) {
        currentRoute.fromAirport = airport;
        if (!currentRoute.from) {
          currentRoute.from = yourPositionLabel;
        }
      } else {
        currentRoute.toAirport = airport;
        if (!currentRoute.to) {
          currentRoute.to = yourPositionLabel;
        }
      }
    }

    function setEditingAirport(
      airport: PortInfo,
      index: number,
      isFromAirport: boolean
    ): void {
      editingAirport.value = airport;
      editingAirportIndex.value = index;
      editingIsFromAirport.value = isFromAirport;
      editing.value = true;
    }

    function clearEditingAirport(): void {
      editingAirport.value = undefined;
      editingAirportIndex.value = undefined;
      editingIsFromAirport.value = true;
      editing.value = false;
    }

    function updateAirport(airport: FlightAutoCompleteSuggestion): void {
      const departureAirportIndex = 0;
      const homeBoundAirportIndex = routes.value.length - 1;

      const isEditingHomeBoundAirport =
        editingAirportIndex.value === homeBoundAirportIndex &&
        editingIsFromAirport.value === false;
      const isEditingDepartureAirport =
        editingAirportIndex.value === departureAirportIndex &&
        editingIsFromAirport.value === true;

      if (editingAirportIndex.value !== undefined) {
        if (
          shouldUpdateHomeAirport.value &&
          (isEditingDepartureAirport || isEditingHomeBoundAirport)
        ) {
          setAriport(homeBoundAirportIndex, false, {
            code: airport.iata,
            name: airport.tooltip,
            label: airport.label,
          });

          setAriport(departureAirportIndex, true, {
            code: airport.iata,
            name: airport.tooltip,
            label: airport.label,
          });
        } else {
          setAriport(editingAirportIndex.value, editingIsFromAirport.value, {
            code: airport.iata,
            name: airport.tooltip,
            label: airport.label,
          });
        }
        clearEditingAirport();
      }
    }

    function setShouldUpdateHomeAirport(): void {
      shouldUpdateHomeAirport.value = !shouldUpdateHomeAirport.value;

      if (shouldUpdateHomeAirport.value) {
        const departureAirport = routes.value[0]?.fromAirport;
        const homeBoundAirport =
          routes.value[routes.value.length - 1]?.toAirport;

        if (departureAirport && homeBoundAirport) {
          homeBoundAirport.code = departureAirport.code;
          homeBoundAirport.name = departureAirport.name;
          homeBoundAirport.label = departureAirport.label;
        }
      }
    }

    return {
      // State
      routes,
      editingAirport,
      shouldUpdateHomeAirport,

      // Getters
      isButtonDisabled,
      isEditing,
      isEditingFrom,

      // Actions
      toggleRoute,
      toggleDirectFlight,
      initializeRoutes,
      setEditingAirport,
      clearEditingAirport,
      updateAirport,
      setShouldUpdateHomeAirport,
    };
  }
);
